/**
 * VIB-945: Chunk-load-resiliens.
 *
 * Etter en deploy ber gamle faner om chunk-hasher som ikke lenger finnes.
 * SPA-fallbacken serverer da `index.html` (text/html) i stedet for JS, og den
 * dynamiske importen feiler med "Failed to fetch dynamically imported module"
 * eller en MIME-type-feil. Vi reloader siden for å hente fersk bundle.
 *
 * VIB-2788: reload-vernet var tidligere ETT flagg som sto ut økten, så fane
 * nummer to, tre og fire reloadet aldri igjen uansett hvor lang tid som gikk.
 * En økt som varer en arbeidsdag ser flere deploys, og bare den første ble
 * håndtert. Nå er vernet bundet til TID og ANTALL i stedet: se `tryReload`.
 */

const RELOAD_STATE_KEY = "vib-chunk-reload";

/**
 * Minste tid mellom to reloads. En ekte reload-loop går på sekunder (siden
 * laster, chunken feiler igjen, vi reloader), så ett minutt stopper den, mens
 * to deploys en halvtime fra hverandre begge får sin reload.
 */
export const RELOAD_MIN_GAP_MS = 60_000;

/**
 * Tak på antall reloads i én økt.
 *
 * Tidsvinduet alene er ikke nok: en chunk som er borte for godt (en fil som
 * faktisk mangler i deployen) blir aldri bedre av en reload, og uten taket
 * ville brukeren fått siden lastet på nytt hvert minutt for alltid. Med taket
 * koster den feilen høyst tre reloads, og så faller vi tilbake på en ærlig
 * feilmelding.
 */
export const RELOAD_MAX_PER_SESSION = 3;

/** Meldingen brukeren skal se når en reload ikke løste det. */
export const CHUNK_RELOAD_MESSAGE =
  "Appen er oppdatert. Last siden på nytt og prøv igjen.";

/**
 * True hvis feilen ser ut som en chunk-/dynamisk-import-feil (stale bundle).
 * Dekker meldingene Chrome/Edge, Firefox og Safari gir, samt Vites egne.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  if (error instanceof Error && error.name === "ChunkLoadError") return true;
  const msg = error instanceof Error ? error.message : String(error);
  return (
    /failed to fetch dynamically imported module/i.test(msg) || // Chrome/Edge
    /error loading dynamically imported module/i.test(msg) || // Firefox
    /importing a module script failed/i.test(msg) || // Safari
    /failed to load module script/i.test(msg) || // MIME-mismatch (text/html)
    /expected a javascript.*module.*mime type/i.test(msg) ||
    /chunkloaderror/i.test(msg)
  );
}

/**
 * Feilmelding å vise brukeren.
 *
 * Egen funksjon, ikke en `if` på hvert kallsted: en chunk-feil er den samme
 * situasjonen uansett hvor i appen den dukker opp, og den rå teksten
 * («Failed to fetch dynamically imported module …») sier ingenting til en
 * ansatt som bare prøvde å laste opp et tilbud.
 */
export function chunkErrorMessage(error: unknown): string {
  if (isChunkLoadError(error)) return CHUNK_RELOAD_MESSAGE;
  return error instanceof Error ? error.message : String(error);
}

type ReloadState = { n: number; at: number };

function readState(): ReloadState | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_STATE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as ReloadState).n !== "number" ||
      typeof (parsed as ReloadState).at !== "number"
    ) {
      return null;
    }
    return parsed as ReloadState;
  } catch {
    // Utilgjengelig eller ødelagt sessionStorage: behandles som «ingen reloads
    // ennå». Da reloader vi uten loop-vern, og en fungerende side er bedre enn
    // en ødelagt.
    return null;
  }
}

/**
 * Reload siden hvis vernet tillater det. Returnerer `true` hvis en reload ble
 * trigget, `false` hvis den ble avslått (for nylig, eller taket er nådd).
 *
 * `false` er IKKE en stille fiasko: kallstedet skal da la den opprinnelige
 * feilen boble opp, slik at brukeren får se hva som gikk galt. Se VIB-2788.
 */
export function reloadForChunkError(now: number = Date.now()): boolean {
  const state = readState();
  if (state) {
    if (state.n >= RELOAD_MAX_PER_SESSION) return false;
    if (now - state.at < RELOAD_MIN_GAP_MS) return false;
  }
  try {
    sessionStorage.setItem(
      RELOAD_STATE_KEY,
      JSON.stringify({ n: (state?.n ?? 0) + 1, at: now } satisfies ReloadState),
    );
  } catch {
    // sessionStorage utilgjengelig (privat modus e.l.) — fall gjennom og
    // reload uten loop-vern.
  }
  window.location.reload();
  return true;
}

/**
 * Lytteren for Vites `vite:preloadError` (VIB-2788).
 *
 * `preventDefault()` KUN når vi faktisk reloader. Kalles den uansett, lar Vite
 * være å re-throwe, og preload-hjelperen resolver til `undefined` i stedet for
 * å feile. Kallstedet gjør da `const { X } = undefined` og krasjer med «Cannot
 * destructure property 'X' from null or undefined value» — en melding som ikke
 * sier noe om at en chunk ikke lot seg laste. Reloader vi ikke, skal feilen
 * boble videre, så kallstedet kan vise `chunkErrorMessage`.
 *
 * Samme betingelse som `lazy-with-reload.ts` allerede bruker. Den bor her, og
 * ikke inline i `main.tsx`, fordi en regel ingen test når er en regel som
 * stille kan bli borte igjen — slik denne ble.
 */
export function onPreloadError(event: Pick<Event, "preventDefault">): boolean {
  if (!reloadForChunkError()) return false;
  event.preventDefault();
  return true;
}
